import { Box } from '@atgfr8/atg-ui-tailwind';
import {
  equipmentTypes,
  intermodalEquipmentTypes,
  loadModesTypes,
} from '@common/commonLists';
import { BasicModal, RadioGroup, SelectInput } from '@components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { addNewLoadToOrder, orderStore, updateOrderState } from '@store/orders';
import { defaultLoadEditViewModel } from '@store/orders/defaultVals';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { printLog } from '@utils/utils';
import { createEffect, onCleanup } from 'solid-js';
import * as yup from 'yup';

type TabActionFormProps = {
  onClose: () => void;
  onSubmit: (formData: AddNewCarrier) => void;
};

export const AddCarrierModalId = 'AddCarrierModal';

export type AddNewCarrier = {
  modeType: string;
  equipmentType: string;
  bidType: string;
};

const AddNewCarrier = (props: TabActionFormProps) => {
  const { form, data, setFields, errors } = createForm<AddNewCarrier>({
    initialValues: {
      modeType: '',
      equipmentType: '',
      bidType: '',
    },
    extend: validator({
      schema: yup.object().shape({
        modeType: yup.string().required('Mode Type is required'),
        equipmentType: yup.string().required('Equipment Type is required'),
        bidType: yup.string().required('Bid Type is required'),
      }),
    }),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  onCleanup(() => {
    props.onClose();
  });

  const handleSubmit = (values: AddNewCarrier) => {
    printLog(values, 'values');
    props.onClose();
    props.onSubmit(values);
    addNewLoadToOrder({
      ...defaultLoadEditViewModel,
      cargoValueIsHigh: undefined,
      mode: values.modeType,
      equipment: values.equipmentType,
      customerId: orderStore.order.customerId,
      // fix this when api is fixed
      // bidType: values.bidType,
    } as ILoadEditViewModel);
    updateOrderState({ bidType: values.bidType });
    setFields({
      modeType: '',
      equipmentType: '',
      bidType: '',
    });
  };

  createEffect(() => {
    if (data().modeType && data().equipmentType && data().bidType) {
      handleSubmit(data() as AddNewCarrier);
    }
  });

  const style = {
    header: true,
    footer: true,
    onCloseText: 'Cancel',
    onSubmitText: 'Submit',
    modalStyles: {
      backgroundColor: 'white',
    },
    footerContainerClass: 'flex justify-end items-center gap-2 flex-wrap',
    closeButtonVariant: 'outlined',
    showClose: true,
  };

  return (
    <BasicModal
      id={AddCarrierModalId}
      title="Select a Mode"
      onClose={props.onClose}
      showClose={false}
      footer={false}
      modalStyles={style as unknown as Record<string, string>}
      width={orderStore.isMobile ? '95vw' : '500px'}
    >
      <form ref={form}>
        <Box class="flex flex-col gap-y-4 mt-[8px]">
          <SelectInput
            label="Mode Types"
            menuItems={loadModesTypes.map((mode) => {
              return {
                label: mode,
                value: mode,
              };
            })}
            value={data().modeType}
            onChange={(newMode) => {
              setFields('modeType', newMode);
              const currentEquipment = data().equipmentType;
              const isValidForNewMode =
                newMode === 'Intermodal'
                  ? intermodalEquipmentTypes.includes(currentEquipment)
                  : equipmentTypes.some((e) => e.value === currentEquipment);
              if (!isValidForNewMode) {
                setFields('equipmentType', newMode === 'LTL' ? 'V, Van' : '');
              }
            }}
            error={(() => {
              const v = errors().modeType;
              return Array.isArray(v) ? v.join(', ') : v;
            })()}
            dataTestId={formatDataTestId('Mode Types', 'selectInput')}
          />
          <SelectInput
            label="Equipment Types"
            menuItems={
              data().modeType === 'Intermodal'
                ? intermodalEquipmentTypes.map((s) => ({ label: s, value: s }))
                : equipmentTypes.map((e) => ({
                    label: e.label,
                    value: String(e.value),
                  }))
            }
            value={data().equipmentType}
            onChange={(value) => setFields('equipmentType', value)}
            error={(() => {
              const v = errors().equipmentType;
              return Array.isArray(v) ? v.join(', ') : v;
            })()}
            dataTestId={formatDataTestId('Equipment Types', 'selectInput')}
          />
          <RadioGroup
            label="Bid Type"
            direction="horizontal"
            value={data().bidType}
            options={[
              { label: 'Spot Market', value: 'Spot Market' },
              { label: 'Contract', value: 'Contract' },
            ]}
            onChange={(value) => setFields('bidType', value)}
            error={errors().bidType}
          />
        </Box>
      </form>
    </BasicModal>
  );
};

export default AddNewCarrier;
